import type { ComponentProps, ReactNode } from 'react';
import type { MagnifyButtonProps } from './MagnifyButton';
import { MagnifyButton } from './MagnifyButton';
import styles from './styles.module.css';
import type { MapCenter } from './types';

const isRetina = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  // requesting retina picture is up to the device capabilities;
  return window.devicePixelRatio ? window.devicePixelRatio > 1 : false;
};

/**
 * Make the url of the map
 *
 * @param width
 * @param height
 * @param center
 * @return {string}
 */
const makeMapUrl = (width: number, height: number, center: MapCenter): string => {
  const mapId = process.env.MAP_BOX_MAP_ID || '';
  const token = process.env.MAP_BOX_API_KEY || '';

  return (
    `https://api.mapbox.com/styles/v1/ornikar/${mapId}/static/` +
    `${center.long.toString(10)},${center.lat.toString(10)},${center.zoom.toString(10)}/` +
    `${Math.round(width)}x${Math.round(height)}${isRetina() ? '@2x' : ''}` +
    `?access_token=${token}`
  );
};

type ImgProps = ComponentProps<'img'>;

export interface MapPictureProps {
  width: number;
  height: number;
  center: MapCenter;
  alt?: string;
  onLoad: ImgProps['onLoad'];
  onError: ImgProps['onError'];
  onMagnifyMapClick?: MagnifyButtonProps['onClick'];
}

export function MapPicture({
  width,
  height,
  center,
  alt,
  onLoad,
  onError,
  onMagnifyMapClick,
}: MapPictureProps): ReactNode {
  return (
    <div className={styles.MapPicture}>
      <img src={makeMapUrl(width, height, center)} alt={alt} onLoad={onLoad} onError={onError} />
      {onMagnifyMapClick && <MagnifyButton onClick={onMagnifyMapClick} />}
    </div>
  );
}
